import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { DispoCalendarEvent } from '@shared/types'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'

interface OutOfRangeIndicatorProps {
  events: DispoCalendarEvent[]
  side: 'left' | 'right'
  onEventClick?: (event: DispoCalendarEvent) => void
}

function eventSummary(event: DispoCalendarEvent): string {
  return (
    event.title ||
    event.ticketTitle ||
    event.companyName ||
    event.planningAdditionalTitle ||
    event.supportTypeName ||
    event.type
  )
}

function formatTs(ts: number) {
  return format(new Date(ts * 1000), 'HH:mm', { locale: de })
}

export function DispositionCalendarOutOfRangeIndicator({
  events,
  side,
  onEventClick,
}: OutOfRangeIndicatorProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onOutside)
    return () => document.removeEventListener('mousedown', onOutside)
  }, [open])

  const Icon = side === 'left' ? ChevronLeft : ChevronRight
  const panelSide = side === 'left' ? 'left-0' : 'right-0'

  return (
    <div
      ref={ref}
      className="w-full h-full relative"
    >
      <button
        type="button"
        title={`${events.length} Termin${events.length !== 1 ? 'e' : ''} außerhalb des sichtbaren Zeitraums`}
        onClick={() => setOpen((o) => !o)}
        className="flex flex-col items-center justify-center w-full h-full text-[9px] font-bold leading-none
          bg-gray-300/80 hover:bg-gray-400/80 text-gray-700 cursor-pointer select-none"
      >
        <Icon className="w-2.5 h-2.5" />
        <span>{events.length}</span>
      </button>

      {open && (
        <div
          className={`absolute top-full ${panelSide} z-[200] mt-1 min-w-[220px] max-w-[300px]
            bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-xs`}
          style={{ maxHeight: 240, overflowY: 'auto' }}
        >
          <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100">
            {side === 'left' ? 'Vor' : 'Nach'} dem sichtbaren Zeitraum
          </div>
          {events.map((event) => {
            const timeStr = event.endTs
              ? `${formatTs(event.startTs)}–${formatTs(event.endTs)}`
              : formatTs(event.startTs)
            const itemContent = (
              <>
                <span className="font-medium text-gray-800 truncate">{eventSummary(event)}</span>
                <span className="text-gray-500">{timeStr}</span>
                {(event.companyName || event.ticketTitle) && (
                  <span className="text-gray-400 truncate">
                    {event.companyName ?? event.ticketTitle}
                  </span>
                )}
              </>
            )
            return onEventClick ? (
              <button
                key={event.id}
                type="button"
                onClick={() => {
                  onEventClick(event)
                  setOpen(false)
                }}
                className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex flex-col gap-0.5 cursor-pointer"
              >
                {itemContent}
              </button>
            ) : (
              <div key={event.id} className="px-3 py-1.5 flex flex-col gap-0.5 cursor-default">
                {itemContent}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
